import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { useApolloClient, useQuery } from '@apollo/client';
import { GET_CATEGORY_BY_PARENTID } from '@/core/graphql/queries/getCategories';
import { ENV } from '@/core/config/env';
import { Category } from '@/core/models/categories/category';
import ProductSlider from '../product/ProductSlider';
import { ProductItem } from '@/core/models/products/productList';
import { GET_PRODUCTS } from '@/core/graphql/queries/getProductList';

const bgColors = ['#1D5CA7', '#A46000', '#00897B', '#789262', '#9B111E'];
const fallbackImages = [
  '/images/home-product-slider/engine-spares.webp',
  '/images/home-product-slider/mechanical-parts.webp',
  '/images/home-product-slider/electrical-spares.webp',
  '/images/home-product-slider/tyres-and-exhausts.webp',
  '/images/home-product-slider/car-care.webp',
];

interface CategoryWithProductProps {
  categoryId?: number;
}

export default function CategoryWithProduct({ categoryId = ENV.CATEGORY_ID }: CategoryWithProductProps) {
  const client = useApolloClient();
  const [categoryProducts, setCategoryProducts] = useState<Record<string, ProductItem[]>>({});

  const { loading: categoryLoading, data: categoryData } = useQuery(GET_CATEGORY_BY_PARENTID, {
    variables: { id: categoryId, size: 20 }
  });

  const categories: Category[] = useMemo(
    () => categoryData?.getMstCategoryByParentId?.result || [],
    [categoryData]
  );

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const responses = await Promise.all(
          categories.map(category =>
            client.query({
              query: GET_PRODUCTS,
              variables: {
                domainCategoryIds: String(category.categoryId),
                page: 1,
                size: 5
              }
            })
          )
        );

        const newCategoryProducts: Record<string, ProductItem[]> = {};
        responses.forEach((res, idx) => {
          const categoryId = String(categories[idx].categoryId);
          newCategoryProducts[categoryId] = res.data.getPrdProductList?.result || [];
        });

        setCategoryProducts(newCategoryProducts);
      } catch (err) {
        console.error('Failed to fetch products', err);
      }
    };

    if (categories.length > 0) {
      fetchProducts();
    }
  }, [categories, client]);

  if (categoryLoading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      {categories.map((category, index) => {
        const products = categoryProducts[String(category.categoryId)] || [];
        if (products.length === 0) return null;

        const colorIndex = index % bgColors.length;
        const bgColor = bgColors[colorIndex];
        const imageSrc = fallbackImages[colorIndex % fallbackImages.length];

        return (
          <div className="flex flex-col md:flex-row gap-6 pb-6 px-[15px] h-auto md:h-[411px]" key={index}>
            <div className="w-full md:w-1/2 lg:w-1/3 xl:w-1/4 2xl:w-1/3 flex-shrink-0 flex-grow-0">
              <div className="category-box overflow-hidden relative h-[300px] md:h-[411px]">
                <Image
                  src={imageSrc}
                  width={606}
                  height={432}
                  className="w-full h-full object-cover"
                  alt={category.categoryName}
                />
                <div className="p-8 absolute bottom-0 w-full">
                  <div
                    className="absolute h-[200px] -left-10 sm:-top-20 -bottom-28 w-full rotate-3 scale-[1.3] z-0"
                    style={{ backgroundColor: bgColor }}
                  ></div>
                  <h3 className="text-2xl text-white relative z-10 sm:bottom-5">
                    {category.categoryName}
                  </h3>
                </div>
              </div>
            </div>
            <div className="w-full md:w-1/2 lg:w-2/3 xl:w-3/4 2xl:w-2/3 flex-shrink-0 flex-grow-0 overflow-hidden shadow-[0px_4px_44px_0px_rgb(0,0,0,0.1)] h-[300px] md:h-[411px]">
              <ProductSlider products={products} />
            </div>
          </div>
        );
      })}
    </div>
  );
}